import {
  useRef,
  useState,
} from "react";

interface SegmentedDateInputProps {
  /** Base id for the segment inputs (`${id}-month` etc.). */
  id: string;
  /** Latest allowed date (ISO `YYYY-MM-DD`), e.g. today for a date of birth. */
  max?: string | undefined;
  /**
   * Earliest allowed date (ISO `YYYY-MM-DD`), e.g. today for a travel
   * document's expiry. The mirror of `max`, and the two are never both set:
   * a field is either in the past or in the future, never bracketed.
   */
  min?: string | undefined;
  /**
   * Seeds the segments on mount (ISO `YYYY-MM-DD`). The component stays
   * uncontrolled afterwards — a parent applying a new value remounts it via
   * `key` (see SecretEntryPage's prefill nonce).
   */
  initialIso?: string | undefined;
  /** Called with the composed ISO date, or `""` while incomplete/invalid. */
  onChange: (iso: string) => void;
  /**
   * Id of the message saying why the server refused this date. The segments
   * are marked invalid and described by it, beside any error of their own.
   */
  errorId?: string | undefined;
}

interface ComposedDate {
  iso: string;
  error: string | null;
}

// Exported for unit tests — this pure helper carries all of the component's
// date correctness.
export function composeIso(
  month: string,
  day: string,
  year: string,
  max?: string,
  min?: string,
): ComposedDate {
  // Still typing — no value yet, but no error either.
  if (!month || !day || year.length < 4) {
    return { iso: "", error: null };
  }
  const m = Number(month);
  const d = Number(day);
  const y = Number(year);
  const date = new Date(Date.UTC(y, m - 1, d));
  const isReal =
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d;
  if (!isReal) {
    return { iso: "", error: "That date doesn't exist — double-check it." };
  }
  const iso = `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  if (max && iso > max) {
    return { iso: "", error: "This date can't be in the future." };
  }
  if (min && iso < min) {
    return { iso: "", error: "This date can't be in the past." };
  }
  return { iso, error: null };
}

function digits(raw: string, maxLen: number): string {
  return raw.replace(/\D/g, "").slice(0, maxLen);
}

/**
 * Month / Day / Year date entry. The native `type="date"` picker is built for
 * nearby dates — for a date of birth it forces paging a calendar back decades,
 * so we use three plain numeric fields instead (the standard DOB pattern).
 */
function segmentsFromIso(iso: string | undefined): {
  month: string;
  day: string;
  year: string;
} {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return { month: "", day: "", year: "" };
  }
  return { month: iso.slice(5, 7), day: iso.slice(8, 10), year: iso.slice(0, 4) };
}

export function SegmentedDateInput({
  id,
  max,
  min,
  initialIso,
  onChange,
  errorId,
}: SegmentedDateInputProps) {
  const initial = segmentsFromIso(initialIso);
  const [month, setMonth] = useState(initial.month);
  const [day, setDay] = useState(initial.day);
  const [year, setYear] = useState(initial.year);
  const [error, setError] = useState<string | null>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Reads every segment from the DOM rather than from render-closure state:
  // two change events can land within one render window (browser autofill,
  // paste), and a closure over the sibling segments would clobber the one
  // that changed first.
  const syncFromDom = () => {
    const next = {
      month: digits(monthRef.current?.value ?? "", 2),
      day: digits(dayRef.current?.value ?? "", 2),
      year: digits(yearRef.current?.value ?? "", 4),
    };
    setMonth(next.month);
    setDay(next.day);
    setYear(next.year);
    const composed = composeIso(next.month, next.day, next.year, max, min);
    setError(composed.error);
    // Surface an incomplete or impossible combination through native form
    // validity: with e.g. a two-digit year every segment is non-empty, so the
    // segments' own `required` passes and without this the form would submit
    // an empty value. The incomplete-date message is deliberately not shown
    // inline (it would be noise on every keystroke) — it only backs the
    // browser's submit-time bubble.
    yearRef.current?.setCustomValidity(
      composed.error ?? (composed.iso ? "" : "Enter the full date."),
    );
    onChange(composed.iso);
    return next;
  };

  const describedBy =
    [error ? `${id}-error` : null, errorId].filter(Boolean).join(" ") ||
    undefined;
  const segmentClass = `block w-full rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-catch-red focus:ring-catch-red px-3 py-2 border text-center ${
    describedBy ? "border-red-400" : "border-gray-300"
  }`;
  // Ties the inline error text to each segment for screen readers — the red
  // border alone carries no reason.
  const segmentAria = {
    "aria-invalid": describedBy ? true : undefined,
    "aria-describedby": describedBy,
  };

  return (
    <div>
      <div className="grid grid-cols-[1fr_1fr_1.4fr] gap-2">
        <div>
          <label
            htmlFor={`${id}-month`}
            className="block text-xs text-gray-500 mb-1"
          >
            Month
          </label>
          <input
            id={`${id}-month`}
            ref={monthRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="MM"
            required
            {...segmentAria}
            value={month}
            onChange={() => {
              if (syncFromDom().month.length === 2) dayRef.current?.focus();
            }}
            className={segmentClass}
          />
        </div>
        <div>
          <label
            htmlFor={`${id}-day`}
            className="block text-xs text-gray-500 mb-1"
          >
            Day
          </label>
          <input
            id={`${id}-day`}
            ref={dayRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="DD"
            required
            {...segmentAria}
            value={day}
            onChange={() => {
              if (syncFromDom().day.length === 2) yearRef.current?.focus();
            }}
            className={segmentClass}
          />
        </div>
        <div>
          <label
            htmlFor={`${id}-year`}
            className="block text-xs text-gray-500 mb-1"
          >
            Year
          </label>
          <input
            id={`${id}-year`}
            ref={yearRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="YYYY"
            required
            {...segmentAria}
            value={year}
            onChange={() => syncFromDom()}
            className={segmentClass}
          />
        </div>
      </div>
      {error && (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
